/**
 * High-visibility sticky banner shown across all tenant routes when an admin
 * is impersonating a tenant. Click "End Session" to revoke and close.
 */

import React, { useEffect, useState } from 'react';

import { useImpersonation } from '../contexts/ImpersonationContext';

function relative(started: string): string {
  const ms = Date.now() - new Date(started).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  return `${Math.floor(min / 60)}h ${min % 60}m ago`;
}

export const ImpersonationBanner: React.FC = () => {
  const { event, isImpersonating, end } = useImpersonation();
  const [, force] = useState(0);

  // Re-render every 30s so the relative time stays fresh.
  useEffect(() => {
    if (!isImpersonating) return;
    const t = setInterval(() => force((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, [isImpersonating]);

  if (!event) return null;

  return (
    <div
      data-testid="impersonation-banner"
      role="alert"
      className="sticky top-0 z-40 w-full bg-orange-600 text-white px-4 py-2 flex items-center justify-between shadow"
    >
      <div className="text-sm">
        Impersonating <strong>{event.target.username}</strong>{' '}
        ({event.target.email}) — Reason: <strong>{event.reason_code}</strong>
        {event.reason_note ? <> ({event.reason_note})</> : null}{' '}
        — started {relative(event.started_at)}
      </div>
      <button
        data-testid="end-impersonation"
        onClick={async () => {
          await end();
          window.close();
        }}
        className="ml-4 bg-white text-orange-700 font-medium rounded px-3 py-1 hover:bg-gray-100"
      >
        End Session
      </button>
    </div>
  );
};
